import { InboxOutlined } from '@ant-design/icons';
import { Col, DatePicker, Form, Input, Select, Upload, message } from 'antd';
import { FormInstance, useForm } from 'antd/lib/form/Form';
import { RcFile } from 'antd/lib/upload';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useShallow } from 'zustand/react/shallow';
import { ISiteAccount } from '~/@core/domain/model/site/SiteAccount';
import DefaultRow from '~/components/bosons/DefaultRow';
import AutoCategory from '~/domain/model/ServiceRequests/AutoCategory';
import Category from '~/domain/model/ServiceRequests/Category';
import SubCategory from '~/domain/model/ServiceRequests/SubCategory';
import { selectors } from '~/store/modules';
import useServiceRequestsStore, {
  IFormFilter
} from '~/zustand/serviceRequests/useServiceRequestsStore';
import * as S from './ServiceRequestsFilters.styled';

export default function ServiceRequestsFilters() {
  const [formFilter] = useForm<IFormFilter>();
  const profile = useSelector(selectors.ui.auth.getProfile);

  const {
    setDataStore,
    setFormInstance,
    filters,
    validateEmailAndLoadFilters,
    isFiltersSuccessful,
    getSubCategories,
    getAutoCategories,
    sendAttachmentFile,
    createServiceRequest,
    clearAttachmentFile,
    buttonDisabled
  } = useServiceRequestsStore(
    useShallow(state => ({
      setDataStore: state.setData,
      setFormInstance: state.setFormInstance,
      filters: state.filters,
      validateEmailAndLoadFilters: state.validateEmailAndLoadFilters,
      isFiltersSuccessful: state.isFiltersSuccessful,
      getSubCategories: state.getSubCategories,
      getAutoCategories: state.getAutoCategories,
      sendAttachmentFile: state.sendAttachmentFile,
      createServiceRequest: state.createServiceRequest,
      clearAttachmentFile: state.clearAttachmentFile,
      buttonDisabled: state.buttonDisabled
    }))
  );

  useEffect(() => {
    const instance = {
      validateFields: formFilter.validateFields,
      getFieldValue: formFilter.getFieldValue,
      getFieldsValue: formFilter.getFieldsValue,
      setFieldValue: formFilter.setFieldValue,
      setFieldsValue: formFilter.setFieldsValue
    } as Partial<FormInstance<IFormFilter>>;

    setFormInstance(instance);
  }, [formFilter, setFormInstance]);

  useEffect(() => {
    validateEmailAndLoadFilters(profile.veiling_claim_type_email);
  }, [validateEmailAndLoadFilters]);

  const onSubmit = async () => {
    try {
      await formFilter.validateFields();
    } catch (validationError) {
      message.error(
        'Alguns campos obrigatórios não foram preenchidos corretamente. Por favor, preencha-os e tente novamente.'
      );
      return;
    }

    try {
      const response = await createServiceRequest();

      if (response && typeof response === 'object' && 'erro' in response) {
        const errorMessage = response.erro;
        message.error(errorMessage);
        return;
      }

      formFilter.resetFields();
      clearAttachmentFile();
      message.success(`Solicitação criada com sucesso! Número do chamado: ${response}`);
    } catch (submitError: any) {
      console.error('Erro ao enviar a solicitação:', submitError);
      const backendMessage = submitError?.response?.data?.message;

      message.error(
        backendMessage ??
        'Ocorreu um erro ao enviar a solicitação. Tente novamente mais tarde.'
      );
    }
  };

  const handleCategoryOnClear = () => {
    formFilter.setFieldsValue({
      categoryId: undefined
    });
  };

  const handleCategoryOnChange = () => {
    formFilter.setFieldsValue({
      subCategoryId: undefined,
      autoCategoryId: undefined
    });

    getSubCategories();
    getAutoCategories();
  };

  const handleSubgroupOnClear = () => {
    formFilter.setFieldsValue({
      subCategoryId: undefined
    });
  };

  const handleSubgroupOnChange = () => {
    formFilter.setFieldsValue({
      autoCategoryId: undefined,
      menuOrPage: undefined
    });

    getAutoCategories();
  };

  const handleAutoCategoryOnClear = () => {
    formFilter.setFieldsValue({
      autoCategoryId: undefined,
      menuOrPage: undefined
    });
  };

  const handleAutoCategoryOnChange = (autoCategoryId: number) => {
    const autoCategoryName = filters.autoCategories.data.find(
      (item: AutoCategory) => item.account === autoCategoryId
    )?.name;
    const failureAutoCategory = autoCategoryName?.includes('Falha') ?? false;
    const reportsAutoCategory =
      autoCategoryName?.includes('Relatório') ?? false;
    const billingOutsideSpAutoCategory = (autoCategoryName?.includes('Faturamento') && autoCategoryName?.includes('Produtor Fora Estado São Paulo')) ?? false;
    const billingInsideSpAutoCategory = (autoCategoryName?.includes('Faturamento') && autoCategoryName?.includes('Produtor Estado São Paulo')) ?? false;
    setDataStore(state => {
      state.filters.autoCategories.isFailure = failureAutoCategory;
      state.filters.autoCategories.isReports = reportsAutoCategory;
      state.filters.autoCategories.isBillingOutsideSP = billingOutsideSpAutoCategory;
      state.filters.autoCategories.isBillingInsideSP = billingInsideSpAutoCategory;
    });
  };

  const preventLetters = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/[0-9]/.test(e.key)) {
      e.preventDefault();
    }
  }

  const handleBeforeInput = (e: React.FormEvent<HTMLInputElement>) => {
    const input = (e as any).nativeEvent.data;
    if (input && !/^\d+$/.test(input)) {
      e.preventDefault();
    }
  };

  return (
    <Form
      layout="vertical"
      form={formFilter}
      initialValues={filters.formFilter}
    >
      <DefaultRow>
        <Col lg={24}>
          <Form.Item
            label="Chave / Nome Fantasia"
            name="siteId"
            rules={[
              {
                required: true,
                message: 'Selecione corretamente uma chave de produtor'
              }
            ]}
          >
            <Select
              placeholder="Selecione uma chave"
              showSearch
              allowClear
              showArrow
              loading={filters.sites.isLoading}
              disabled={filters.sites.isLoading}
              optionFilterProp="children"
              optionLabelProp="label"
            >
              {filters.sites.data.map((item: ISiteAccount) => (
                <Select.Option
                  key={item.code}
                  value={item.code}
                  label={`${item.code} - ${item.name}`}
                  title={item.name}
                >
                  {`${item.code} - ${item.name}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={12} xs={24}>
          <Form.Item
            label="Categoria"
            name="categoryId"
            rules={[
              {
                required: true,
                message: 'Selecione corretamente uma categoria'
              }
            ]}
          >
            <Select
              placeholder="Selecione uma categoria"
              showSearch
              showArrow
              allowClear
              loading={filters.categories.isLoading}
              disabled={filters.categories.isLoading}
              optionFilterProp="children"
              optionLabelProp="label"
              onClear={handleCategoryOnClear}
              onChange={handleCategoryOnChange}
            >
              {filters.categories.data.map((item: Category) => (
                <Select.Option
                  key={item.account}
                  value={item.account}
                  label={item.name}
                  title={item.name}
                >
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={12} xs={24}>
          <Form.Item
            label="Sub Categoria"
            name="subCategoryId"
            rules={[
              {
                required: true,
                message: 'Selecione corretamente uma sub categoria'
              }
            ]}
          >
            <Select
              placeholder="Selecione uma sub categoria"
              showSearch
              showArrow
              allowClear
              loading={filters.subCategories.isLoading}
              disabled={filters.subCategories.isLoading}
              optionFilterProp="children"
              optionLabelProp="label"
              onClear={handleSubgroupOnClear}
              onChange={handleSubgroupOnChange}
            >
              {filters.subCategories.data.map((item: SubCategory) => (
                <Select.Option
                  key={item.account}
                  value={item.account}
                  label={item.name}
                  title={item.name}
                >
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col lg={24}>
          <Form.Item
            label="Assunto"
            name="autoCategoryId"
            rules={[
              {
                required: true,
                message: 'Selecione corretamente um assunto de solicitação'
              }
            ]}
          >
            <Select
              placeholder="Selecione um assunto de solicitação"
              showSearch
              showArrow
              allowClear
              loading={filters.autoCategories.isLoading}
              disabled={filters.autoCategories.isLoading}
              optionFilterProp="children"
              optionLabelProp="label"
              onClear={handleAutoCategoryOnClear}
              onChange={handleAutoCategoryOnChange}
            >
              {filters.autoCategories.data.map((item: AutoCategory) => (
                <Select.Option
                  key={item.account}
                  value={item.account}
                  label={item.name}
                  title={item.name}
                >
                  {item.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        {filters.autoCategories.isFailure ? (
          <Col lg={24}>
            <Form.Item label="Informe o menu ou página" name="menuOrPage">
              <Input />
            </Form.Item>
          </Col>
        ) : null}
        {filters.autoCategories.isReports ? (
          <>
            <Col lg={24}>
              <Form.Item label="Informe seu nome completo" name="completeName">
                <Input />
              </Form.Item>
            </Col>
            <Col lg={24}>
              <Form.Item
                label="Informe o período inicial e final"
                name="periodDate"
              >
                <DatePicker.RangePicker
                  key="date-picker"
                  aria-label="Informe o período inicial e final"
                  allowClear
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
          </>
        ) : null}
        {filters.autoCategories.isReports ? (
          <Col lg={24}>
            <S.WarningText>
              Ao abrir este chamado, você concorda com a cobrança no valor
              informado no assunto
            </S.WarningText>
          </Col>
        ) : null}
        {(filters.autoCategories.isBillingInsideSP || filters.autoCategories.isBillingOutsideSP) ? (
          <>
            <Col lg={24}>
              <Form.Item
                label="Informe a quantidade de GFPs"
                name="gfpQuantity"
                rules={[{
                  pattern: /^\d+$/,
                  message: 'Insira apenas números',
                },
                ]}
              >
                <Input
                  inputMode="numeric"
                  pattern="[0-9]*"
                  placeholder="Insira a quantidade"
                  onKeyPress={preventLetters}
                  onBeforeInput={handleBeforeInput}
                />
              </Form.Item>
            </Col>
            <Col lg={24}>
              <Form.Item label="Informe o nome completo do solicitante" name="requesterName">
                <Input placeholder="Insira o nome completo" />
              </Form.Item>
            </Col>
            <Col lg={24}>
              <S.WarningText>
                Anexe as GFPs que serão recepcionadas e a nota fiscal (obrigatório)*.
              </S.WarningText>
            </Col>
          </>
        ) : null}
        <Col lg={24}>
          <Form.Item
            label="Descrição"
            name="description"
            rules={[{ required: true, message: 'Descreva sua solicitação' }]}
          >
            <S.TextAreaDescription />
          </Form.Item>
        </Col>
        <Col lg={24}>
          <Form.Item
            label="Anexos"
            name="attachments"
            rules={[
              () => ({
                validator() {
                  if (filters.autoCategories.isBillingOutsideSP && (!filters.attachments.data || filters.attachments.data.length < 2)) {
                    return Promise.reject(new Error('Anexe pelo menos 2 arquivos'));
                  }
                  if (filters.autoCategories.isBillingInsideSP && (!filters.attachments.data || filters.attachments.data.length < 1)) {
                    return Promise.reject(new Error('Anexe pelo menos 1 arquivo'));
                  }
                  return Promise.resolve();
                }
              })
            ]}
          >
            <>
              {filters.autoCategories.isFailure &&
                filters.attachments.data.length <= 0 ? (
                <>
                  {' '}
                  <S.WarningText>
                    Todo chamado de falha deve conter um anexo relacionado ao
                    problema, favor inserir e prosseguir com a abertura de
                    chamado
                  </S.WarningText>
                </>
              ) : null}
              <Upload.Dragger
                accept=".png, .jpg, .jpeg, .jfif, .pdf, .txt"
                name="attachment"
                fileList={filters.attachments.data}
                multiple={true}
                beforeUpload={(file: RcFile) => {
                  const isLt4M = file.size / 1024 / 1024 < 4;
                  if (!isLt4M) {
                    message.error('O Arquivo deve ser menor que 4MB!');
                  }
                  return isLt4M;
                }}
                onChange={info => sendAttachmentFile(info)}
                customRequest={(options: any) => {
                  const { onSuccess } = options;
                  onSuccess('Ok');
                }}
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined style={{ color: '#F78E1E' }} />
                </p>
                <p className="ant-upload-text">
                  Arraste seu arquivo ou clique para upload
                </p>
                <p className="ant-upload-hint">
                  Formatos de arquivo suportados: JPG / JPEG / PNG / PDF / TXT
                </p>
              </Upload.Dragger>
            </>
          </Form.Item>
        </Col>
        <Col lg={4}>
          <S.Button
            disabled={!isFiltersSuccessful || !filters.email.isValid || buttonDisabled}
            onClick={onSubmit}
            type="primary"
            htmlType="submit"
            aria-label="search"
            size="large"
          >
            Enviar
          </S.Button>
        </Col>
      </DefaultRow>
    </Form >
  );
}
